import { modelConfigs } from './modelConfig';
import { callMiniJSON as callMiniJSONEdge } from './callMiniJSON';
import { callPremiumText as callPremiumTextEdge } from './callPremiumText';
import { deployments } from './env';

export interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export type JsonAnalysisResult = Record<string, unknown>;

/**
 * Healthcheck functions för Azure OpenAI-modeller
 * Testade modellernas tillgänglighet med minimal resursförbrukning
 */

/**
 * Testar om mini-modellen (gpt-5-mini) är tillgänglig
 * @param timeoutMs - Timeout i millisekunder (default: 4000)
 * @returns Promise<boolean> - true om modellen svarar korrekt
 */
export async function pingMini(timeoutMs: number = 4000): Promise<boolean> {
  try {
    console.log('🏥 Health check: Testing gpt-5-mini availability');
    
    const testMessages: Message[] = [
      { role: 'system', content: 'Returnera enbart JSON {ok:true}. Inget annat.' },
      { role: 'user', content: 'ok' }
    ];
    
    // Använd timeout via AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await callMiniJSON(testMessages);
      clearTimeout(timeoutId);
      
      const isOk = response.result && typeof response.result === 'object' && (response.result as any).ok === true;
      console.log(isOk ? '✅ gpt-5-mini health check: OK' : '⚠️ gpt-5-mini health check: Unexpected response');
      return isOk;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  } catch (error) {
    console.error('❌ gpt-5-mini health check failed:', error instanceof Error ? error.message : String(error));
    return false;
  }
}

/**
 * Testar om premium-modellen (gpt-5-premium eller fallback) är tillgänglig
 * @param timeoutMs - Timeout i millisekunder (default: 4000)
 * @returns Promise<boolean> - true om någon premium-modell svarar korrekt
 */
export async function pingPremium(timeoutMs: number = 4000): Promise<boolean> {
  try {
    console.log('🏥 Health check: Testing premium models availability');
    
    const testMessages: Message[] = [
      { role: 'system', content: 'Returnera exakt: OK' },
      { role: 'user', content: 'Svara OK' }
    ];
    
    // Använd timeout via AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const result = await callPremiumWithFallback(testMessages);
      clearTimeout(timeoutId);
      
      const isOk = result && result.trim().toUpperCase() === 'OK';
      console.log(isOk ? '✅ Premium models health check: OK' : '⚠️ Premium models health check: Unexpected response');
      return isOk;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  } catch (error) {
    console.error('❌ Premium models health check failed:', error instanceof Error ? error.message : String(error));
    return false;
  }
}

/**
 * Calls gpt-5-mini deployment with JSON output enforcement
 * Automatically includes the configured system prompt for analysis tasks
 * 
 * @param messages - Array of messages (user prompts, no need to include system prompt)
 * @param maxCompletionTokens - Max tokens for output (default: 2048, for revision use 8000)
 * @returns Parsed JSON object from the model response with usage statistics
 * @throws Error if response is not valid JSON
 */
export async function callMiniJSON(messages: Message[], maxCompletionTokens: number = 2048): Promise<{ result: JsonAnalysisResult, usage?: any }> {
  const config = modelConfigs['gpt-5-mini'];

  try {
    // Ensure system prompt is included (prepend if not present, enhance if present)
    const systemMessage = messages.find(m => m.role === 'system');
    const enhancedMessages = systemMessage 
      ? messages.map(m => m.role === 'system' 
          ? { ...m, content: `${config.systemPrompt}\n\n${m.content}\n\nIMPORTANT: You must respond with valid JSON only. No additional text or formatting.` }
          : m)
      : [
          { role: 'system' as const, content: `${config.systemPrompt}\n\nIMPORTANT: You must respond with valid JSON only. No additional text or formatting.` },
          ...messages
        ];

    return await callMiniJSONEdge(enhancedMessages, maxCompletionTokens);
  } catch (error) {
    console.error('Error in callMiniJSON:', error);
    throw error;
  }
}

/**
 * Premium model fallback system for enhanced resilience and availability
 * 
 * This function implements a two-tier fallback strategy for premium text generation:
 * 1. Primary: Try gpt-5-premium (latest, most capable model)
 * 2. Fallback: Try gpt-4.1-premium (stable, proven model) 
 * 
 * Purpose: Ensure high availability even if primary deployment has issues
 * (rate limits, temporary outages, model updates, etc.)
 * 
 * @param messages - Array of messages (user prompts, no need to include system prompt)
 * @returns Plain text response from whichever premium model succeeds
 * @throws Error if both premium deployments fail
 */
export async function callPremiumWithFallback(messages: Message[]): Promise<string> {
  // Try primary premium deployment (gpt-5-premium)
  try {
    console.log('🔄 Attempting primary premium model: gpt-5-premium');
    
    const primaryConfig = modelConfigs['gpt-5-premium'];
    
    // Ensure system prompt is included
    const systemMessage = messages.find(m => m.role === 'system');
    const enhancedMessages: Message[] = systemMessage 
      ? messages.map(m => m.role === 'system' 
          ? { ...m, content: `${primaryConfig.systemPrompt}\n\n${m.content}` }
          : m)
      : [
          { role: 'system' as const, content: primaryConfig.systemPrompt },
          ...messages
        ];

    const result = await callPremiumTextEdge(enhancedMessages, deployments.premium);
    console.log('✅ Primary premium model (gpt-5-premium) succeeded');
    return result;
    
  } catch (primaryError) {
    console.warn('⚠️ Primary premium model (gpt-5-premium) failed, attempting fallback to gpt-4.1-premium');
    console.warn('Primary error:', primaryError instanceof Error ? primaryError.message : String(primaryError));
    
    // Try fallback premium deployment (gpt-4.1-premium)
    try {
      console.log('🔄 Attempting fallback premium model: gpt-4.1-premium');
      
      const fallbackConfig = modelConfigs['gpt-4.1-premium'];
      
      // Ensure system prompt is included 
      const systemMessage = messages.find(m => m.role === 'system');
      const enhancedMessages: Message[] = systemMessage 
        ? messages.map(m => m.role === 'system' 
            ? { ...m, content: `${fallbackConfig.systemPrompt}\n\n${m.content}` }
            : m)
        : [
            { role: 'system' as const, content: fallbackConfig.systemPrompt },
            ...messages
          ];

      const result = await callPremiumTextEdge(enhancedMessages, deployments.oldPremium);
      console.log('✅ Fallback premium model (gpt-4.1-premium) succeeded');
      return result;
      
    } catch (fallbackError) {
      console.error('❌ Both premium deployments failed');
      console.error('Primary error:', primaryError instanceof Error ? primaryError.message : String(primaryError));
      console.error('Fallback error:', fallbackError instanceof Error ? fallbackError.message : String(fallbackError));
      
      // Both deployments failed - throw comprehensive error
      throw new Error(
        `Both premium deployments failed. Primary (gpt-5-premium): ${primaryError instanceof Error ? primaryError.message : String(primaryError)}. Fallback (gpt-4.1-premium): ${fallbackError instanceof Error ? fallbackError.message : String(fallbackError)}`
      );
    }
  }
}

/**
 * Calls premium deployment with automatic fallback for report generation
 * Automatically includes the configured system prompt for professional reporting
 * Uses resilient fallback system (gpt-5-premium → gpt-4.1-premium)
 * 
 * @param messages - Array of messages (user prompts, no need to include system prompt)
 * @returns Plain text response from the premium model that succeeds
 */
export async function callPremiumText(messages: Message[]): Promise<string> {
  return await callPremiumWithFallback(messages);
}